import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { GridAction } from '@app/shared/custom-component/grid-actions/grid-actions.component';

export interface ColumnConfig {
  key: string;
  label: string;
  disabled?: boolean;
  width?: number | string;
  minWidth?: number;
}

export interface AccordionGridSection {
  id: string;
  title: string;
  isExpanded: boolean;
  showColumnChooser: boolean;
  allColumns: ColumnConfig[];
  visibleColumns: { [key: string]: boolean };
  data: any[];
  dataGridConfig: DataGridVO;
  gridActions: GridAction[];
}

export class AccordionGridSectionManager {
  sections: Map<string, AccordionGridSection> = new Map();

  createSection(
    id: string,
    title: string,
    columns: ColumnConfig[],
    data: any[] = [],
    activeTab: string = 'admin',
    showQueueActions: boolean = true,
    showActionsColumn: boolean = true,
    customButtons: GridAction[] = []
  ): AccordionGridSection {
    const visibleColumns: { [key: string]: boolean } = {};
    columns.forEach(col => {
      visibleColumns[col.key] = true;
    });

    const customHeaders = columns.map(col => ({
      dataField: col.key,
      caption: col.label,
      allowFiltering: true,
      allowSearch: { isSearchEnable: true, type: 'string' }
    }));

    const gridActions: GridAction[] = [
      {
        type: 'export',
        id: `${id}_button_export`,
        title: 'Export',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M12 15.4115C11.8795 15.4115 11.7673 15.3923 11.6635 15.3538C11.5597 15.3154 11.4609 15.2494 11.3672 15.1558L8.25775 12.0463C8.10908 11.8974 8.03567 11.7233 8.0375 11.524C8.0395 11.3247 8.11292 11.1474 8.25775 10.9922C8.41292 10.8373 8.59108 10.7572 8.79225 10.752C8.99358 10.7468 9.17183 10.8218 9.327 10.977L11.25 12.9V5.25C11.25 5.03717 11.3218 4.859 11.4655 4.7155C11.609 4.57183 11.7872 4.5 12 4.5C12.2128 4.5 12.391 4.57183 12.5345 4.7155C12.6782 4.859 12.75 5.03717 12.75 5.25V12.9L14.673 10.977C14.8218 10.8283 14.9984 10.7549 15.2028 10.7568C15.4072 10.7588 15.5871 10.8373 15.7423 10.9922C15.8871 11.1474 15.9621 11.3231 15.9672 11.5192C15.9724 11.7154 15.8974 11.8911 15.7423 12.0463L12.6328 15.1558C12.5391 15.2494 12.4403 15.3154 12.3365 15.3538C12.2327 15.3923 12.1205 15.4115 12 15.4115ZM6.30775 19.5C5.80258 19.5 5.375 19.325 5.025 18.975C4.675 18.625 4.5 18.1974 4.5 17.6923V15.7308C4.5 15.5179 4.57183 15.3398 4.7155 15.1962C4.859 15.0526 5.03717 14.9808 5.25 14.9808C5.46283 14.9808 5.641 15.0526 5.7845 15.1962C5.92817 15.3398 6 15.5179 6 15.7308V17.6923C6 17.7692 6.03208 17.8398 6.09625 17.9038C6.16025 17.9679 6.23075 18 6.30775 18H17.6923C17.7692 18 17.8398 17.9679 17.9038 17.9038C17.9679 17.8398 18 17.7692 18 17.6923V15.7308C18 15.5179 18.0718 15.3398 18.2155 15.1962C18.359 15.0526 18.5372 14.9808 18.75 14.9808C18.9628 14.9808 19.141 15.0526 19.2845 15.1962C19.4282 15.3398 19.5 15.5179 19.5 15.7308V17.6923C19.5 18.1974 19.325 18.625 18.975 18.975C18.625 19.325 18.1974 19.5 17.6923 19.5H6.30775Z" fill="currentColor" />
          </svg>`,
        visible: true
      },
      {
        type: 'columnChooser',
        id: `${id}_button_columnchooser_btn`,
        title: 'Column Chooser',
        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M4.30775 18H8.3365V6H4.30775C4.21792 6 4.14417 6.02883 4.0865 6.0865C4.02883 6.14417 4 6.21792 4 6.30775V17.6923C4 17.7821 4.02883 17.8558 4.0865 17.9135C4.14417 17.9712 4.21792 18 4.30775 18ZM9.8365 18H14.1635V6H9.8365V18ZM15.6635 18H19.6923C19.7821 18 19.8558 17.9712 19.9135 17.9135C19.9712 17.8558 20 17.7821 20 17.6923V6.30775C20 6.21792 19.9712 6.14417 19.9135 6.0865C19.8558 6.02883 19.7821 6 19.6923 6H15.6635V18ZM4.30775 19.5C3.80258 19.5 3.375 19.325 3.025 18.975C2.675 18.625 2.5 18.1974 2.5 17.6923V6.30775C2.5 5.80258 2.675 5.375 3.025 5.025C3.375 4.675 3.80258 4.5 4.30775 4.5H19.6923C20.1974 4.5 20.625 4.675 20.975 5.025C21.325 5.375 21.5 5.80258 21.5 6.30775V17.6923C21.5 18.1974 21.325 18.625 20.975 18.975C20.625 19.325 20.1974 19.5 19.6923 19.5H4.30775Z" fill="currentColor" />
          </svg>`,
        visible: true
      },
      ...customButtons
    ];

    const section: AccordionGridSection = {
      id,
      title,
      isExpanded: true,
      showColumnChooser: false,
      allColumns: columns,
      visibleColumns,
      data,
      dataGridConfig: DataGridVO.create({
        role: activeTab,
        activeTab: activeTab,
        data: data,
        visibleColumns: visibleColumns,
        showQueueActions: showQueueActions,
        disableApplicationClick: false,
        customHeaders: customHeaders,
        showActionsColumn: showActionsColumn
      }),
      gridActions
    };

    this.sections.set(id, section);
    return section;
  }

  getSection(id: string): AccordionGridSection | undefined {
    return this.sections.get(id);
  }

  updateSectionData(id: string, data: any[]): void {
    const section = this.sections.get(id);
    if (section) {
      section.data = data;
      this.updateDataGridConfig(section);
    }
  }

  toggleColumnChooser(id: string): void {
    const section = this.sections.get(id);
    if (section) {
      section.showColumnChooser = !section.showColumnChooser;
      this.updateDataGridConfig(section);
    }
  }

  toggleColumnVisibility(id: string, columnKey: string): void {
    const section = this.sections.get(id);
    if (section && columnKey) {
      const column = section.allColumns.find(c => c.key === columnKey);
      if (column && !column.disabled) {
        section.visibleColumns[columnKey] = !section.visibleColumns[columnKey];
        this.updateDataGridConfig(section);
      }
    }
  }

  closeColumnChooser(id: string): void {
    const section = this.sections.get(id);
    if (section) {
      section.showColumnChooser = false;
      this.updateDataGridConfig(section);
    }
  }

  private updateDataGridConfig(section: AccordionGridSection): void {
    const customHeaders = section.allColumns.map(col => {

      const header: any = {
        dataField: col.key,
        caption: col.label,
        allowFiltering: true,
        allowSearch: { isSearchEnable: true, type: 'string' }
      };

      if (col.key === 'due_date' || col.key === 'transactionDate') {
        header.dataType = 'date';
        header.filterOperations = ['=', '<', '>'];
        header.editorOptions = {
          displayFormat: 'MM/dd/yyyy',
          inputAttr: { inputmode: 'numeric', pattern: '[0-9/]*' }
        };
        header.allowSearch = { isSearchEnable: true, type: 'date' };
      }

      return header;
    });

    section.dataGridConfig = DataGridVO.create({
      role: section.dataGridConfig.role,
      activeTab: section.dataGridConfig.activeTab,
      data: section.data,
      visibleColumns: section.visibleColumns,
      showQueueActions: section.dataGridConfig.showQueueActions,
      disableApplicationClick: section.showColumnChooser,
      customHeaders: customHeaders,
      showActionsColumn: section.dataGridConfig.showActionsColumn
    });
  }

  toggleExpansion(id: string): void {
    const section = this.sections.get(id);
    if (section) {
      section.isExpanded = !section.isExpanded;
    }
  }
}
